import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

const APPS = [
  {
    label: 'Exchange',
    sub: 'Client portal',
    href: 'https://exchange.billerbay.net',
    current: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" aria-hidden>
        <rect width="24" height="24" rx="6" fill="#EFF0FF" />
        <path d="M7 12l3.5 3.5L17 8" stroke="#0410BD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: 'Admin',
    sub: 'HR & people',
    href: 'https://admin.billerbay.net',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" aria-hidden>
        <rect width="24" height="24" rx="6" fill="#FEF3C7" />
        <circle cx="12" cy="9" r="3" stroke="#D97706" strokeWidth="1.8" />
        <path d="M6 19c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="#D97706" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: 'ARC',
    sub: 'Reconciler',
    href: 'https://arc.billerbay.net',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" aria-hidden>
        <rect width="24" height="24" rx="6" fill="#ECFDF5" />
        <path d="M5 12h14M12 5l7 7-7 7" stroke="#059669" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: 'Onboarding',
    sub: 'Onboarding tool',
    href: 'https://onboarding.billerbay.net',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" aria-hidden>
        <rect width="24" height="24" rx="6" fill="#F0F9FF" />
        <path d="M12 4v4M12 16v4M4 12h4M16 12h4" stroke="#0284C7" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="12" cy="12" r="3" stroke="#0284C7" strokeWidth="1.8" />
      </svg>
    ),
  },
  {
    label: 'Clarity',
    sub: 'Dashboards',
    href: 'https://clarity.billerbay.net',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" aria-hidden>
        <rect width="24" height="24" rx="6" fill="#F5F3FF" />
        <rect x="5" y="13" width="3" height="6" rx="1" fill="#7C3AED" />
        <rect x="10.5" y="9" width="3" height="10" rx="1" fill="#7C3AED" />
        <rect x="16" y="5" width="3" height="14" rx="1" fill="#7C3AED" />
      </svg>
    ),
  },
  {
    label: '3CX',
    sub: 'Phone system',
    href: 'https://billerbay.tx.3cx.us/#/office/dashboard',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" aria-hidden>
        <rect width="24" height="24" rx="6" fill="#FFF1F2" />
        <path d="M6.5 6.5c2.5-2.5 9 0 11 5-1 3-4 5-7 4.5" stroke="#E11D48" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="9" cy="15" r="2" stroke="#E11D48" strokeWidth="1.8" />
      </svg>
    ),
  },
];

export function BillerBayAppSwitcher() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'p-2 rounded-md transition-colors',
          open
            ? 'bg-white/15 text-white'
            : 'text-white/60 hover:text-white hover:bg-white/10',
        )}
        aria-label="BillerBay apps"
        title="BillerBay apps"
      >
        {/* 9-dot waffle grid */}
        <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden>
          <circle cx="3" cy="3" r="1.6" />
          <circle cx="9" cy="3" r="1.6" />
          <circle cx="15" cy="3" r="1.6" />
          <circle cx="3" cy="9" r="1.6" />
          <circle cx="9" cy="9" r="1.6" />
          <circle cx="15" cy="9" r="1.6" />
          <circle cx="3" cy="15" r="1.6" />
          <circle cx="9" cy="15" r="1.6" />
          <circle cx="15" cy="15" r="1.6" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 z-50 bg-white border border-[#E3E3F1] rounded-xl shadow-[0_12px_32px_rgba(15,23,42,0.14)] p-3 w-[248px]">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[#BABACE] px-1 mb-2">
            BillerBay Suite
          </p>
          <div className="grid grid-cols-3 gap-1">
            {APPS.map((app) => (
              <a
                key={app.label}
                href={app.href}
                target={app.current ? undefined : '_blank'}
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className={cn(
                  'flex flex-col items-center gap-1.5 px-2 py-2.5 rounded-lg text-center transition-colors',
                  app.current
                    ? 'bg-[#EFF0FF] ring-1 ring-[#0410BD]/20'
                    : 'hover:bg-[#F2F2F8]',
                )}
              >
                {app.icon}
                <span className="text-[11px] font-semibold text-[#12122C] leading-tight">
                  {app.label}
                </span>
                <span className="text-[10px] text-[#BABACE] leading-tight">
                  {app.sub}
                </span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
